import type { ApiResponse } from '../api/contract';
import type { ApiClient } from '../api/http';

export type AuthMode = 'mock' | 'cognito';

export type BackofficePrincipal = ApiResponse<'/api/v1/auth/me', 'get'>['data'];

export interface PrincipalOrigin {
  readonly issuer: string;
  readonly subject: string;
}

export type MockBackofficePrincipal = BackofficePrincipal &
  PrincipalOrigin & {
    readonly mockData: true;
  };

export interface TokenProvider {
  getIdToken(): Promise<string | null>;
  signOut(): Promise<void>;
}

export interface AuthRepository {
  getCurrentUser(idToken: string): Promise<BackofficePrincipal>;
}

export interface AuthEnvironment {
  readonly environment: 'dev' | 'uat' | 'prod';
  readonly authMode: AuthMode;
}

export type AuthSession =
  | {
      readonly status: 'loading';
      readonly principal: null;
      readonly accessToken: null;
    }
  | {
      readonly status: 'unauthenticated';
      readonly principal: null;
      readonly accessToken: null;
    }
  | {
      readonly status: 'authenticated';
      readonly principal: BackofficePrincipal;
      readonly accessToken: string | null;
    }
  | {
      readonly status: 'error';
      readonly principal: null;
      readonly accessToken: null;
    };

export const failClosedTokenProvider: TokenProvider = Object.freeze({
  getIdToken: async () => null,
  signOut: async () => undefined,
});

export function createAuthRepository(apiClient: ApiClient): AuthRepository {
  return {
    async getCurrentUser(idToken: string): Promise<BackofficePrincipal> {
      const result = await apiClient.get<ApiResponse<'/api/v1/auth/me', 'get'>>('/api/v1/auth/me', {
        accessToken: idToken,
      });
      return result.data.data;
    },
  };
}
